import React from 'react';
import FeaturePage from './FeaturePage';
import { Box, Container, Typography, Paper, List, ListItem, ListItemIcon, ListItemText, Divider, Grid } from '@mui/material';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import SortIcon from '@mui/icons-material/Sort';
import CategoryIcon from '@mui/icons-material/Category';
import SummarizeIcon from '@mui/icons-material/Summarize';
import { motion } from 'framer-motion';

import expensesScreenshot from '../assets/expenses-screenshot.png'; // ** Placeholder: Create relevant screenshot **
import depositsScreenshot from '../assets/deposits-screenshot.png'; // ** Placeholder: Create relevant screenshot **

// Animation Variants
const sectionVariants = {
  hidden: { opacity: 0, y: 50 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
};

const itemVariants = {
  hidden: { opacity: 0, x: -20 },
  visible: { opacity: 1, x: 0 }
};

const imageVariants = {
  hidden: { opacity: 0, scale: 0.95 },
  visible: { opacity: 1, scale: 1, transition: { duration: 0.7, ease: "easeOut" } }
};

function FinancialManagementPage() {

  const expenseFeatures = [
    { icon: <ReceiptLongIcon color="error"/>, text: "Log Every Expense: Record all business expenditures, from rent and payroll to marketing and supplies." },
    { icon: <CategoryIcon color="error"/>, text: "Categorize Spending: Assign categories (Rent, Payroll, Marketing, Custom, etc.) for better analysis." },
    { icon: <FilterAltIcon color="error"/>, text: "Advanced Filtering: Filter expenses by date range, price range, and search terms." },
    { icon: <SortIcon color="error"/>, text: "Sort Data: Organize expense records by various fields like date, amount, or category." },
    { icon: <SummarizeIcon color="error"/>, text: "Expense Statistics: View key metrics like total expenses, monthly spending, largest expense, and category totals." },
  ];

  const depositFeatures = [
     { icon: <AccountBalanceIcon color="success"/>, text: "Track Income: Record all incoming funds and deposits." },
     { icon: <ReceiptLongIcon color="success"/>, text: "Detailed Records: Include descriptions, amounts, dates, account details, and reference numbers." },
     { icon: <TrendingUpIcon color="success"/>, text: "Deposit Insights: Monitor total deposits, monthly income, largest deposit, and average deposit amount." },
     { icon: <SummarizeIcon color="success"/>, text: "Clear Financial Overview: Gain a comprehensive view of money coming into the business." },
  ];

  return (
    <FeaturePage title="Financial Management">
      <Container maxWidth="xl" disableGutters sx={{ py: { xs: 4, md: 6 } }}>

        {/* Introductory Text */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: "easeOut", delay: 0.1 }}
          style={{ marginBottom: '4rem', textAlign: 'center' }}
        >
          <Typography variant="h4" component="h1" sx={{ mb: 2, fontWeight: 600 }}>
            Master Your Dealership's Finances
          </Typography>
          <Typography variant="h6" sx={{ mb: 4, color: 'text.secondary', maxWidth: '850px', mx: 'auto' }}>
            Take control of your cash flow. Track every expense and deposit with detailed records and insightful summaries to understand your business's financial health.
          </Typography>
        </motion.div>

        {/* Section 1: Expense Tracking */}
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.1 }} variants={sectionVariants} sx={{ mb: { xs: 6, md: 8 } }}>
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <Box sx={{ maxWidth: '600px', mx: 'auto' }}>
                <Typography variant="h5" sx={{ fontWeight: 500, mb: 3 }}>Comprehensive Expense Tracking</Typography>
                <Typography variant="body1" sx={{ color: 'text.secondary', mb: 3 }}>
                  Monitor where your money is going. Log operational costs, categorize them for clarity, and utilize powerful filtering and sorting to analyze spending patterns.
                </Typography>
                <List dense>
                  {expenseFeatures.map((feature, index) => (
                    <motion.div key={index} variants={itemVariants} custom={index} initial="hidden" animate="visible" transition={{ delay: 0.3 + index * 0.1, duration: 0.5 }}>
                      <ListItem disablePadding sx={{ mb: 1.5, alignItems: 'flex-start' }}>
                        <ListItemIcon sx={{ minWidth: '35px', mt: '4px' }}>
                          {feature.icon}
                        </ListItemIcon>
                        <ListItemText primary={feature.text} primaryTypographyProps={{ variant: 'body1' }} />
                      </ListItem>
                    </motion.div>
                  ))}
                </List>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <motion.div variants={imageVariants}>
                 <Paper elevation={6} sx={{ overflow: 'hidden', borderRadius: '12px', border: '1px solid rgba(0,0,0,0.08)' }}>
                   <Box component="img" src={expensesScreenshot} alt="DealCost Expense Tracking" sx={{ width: '100%', display: 'block' }}/>
                 </Paper>
                 <Typography variant="caption" display="block" align="center" sx={{ mt: 1.5, color: 'text.secondary' }}>
                    Detailed expense logging and analysis.
                 </Typography>
              </motion.div>
            </Grid>
          </Grid>
        </motion.div>

        <Divider sx={{ my: { xs: 4, md: 8 }, borderColor: 'rgba(0,0,0,0.08)' }} />

        {/* Section 2: Deposit Tracking */}
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.1 }} variants={sectionVariants} sx={{ mb: { xs: 4, md: 6 } }}>
           <Grid container spacing={4} alignItems="center" direction={{ xs: 'column-reverse', md: 'row' }}>
             <Grid item xs={12} md={6}>
               <motion.div variants={imageVariants}>
                  <Paper elevation={6} sx={{ overflow: 'hidden', borderRadius: '12px', border: '1px solid rgba(0,0,0,0.08)' }}>
                    <Box component="img" src={depositsScreenshot} alt="DealCost Deposit Tracking" sx={{ width: '100%', display: 'block' }}/>
                  </Paper>
                  <Typography variant="caption" display="block" align="center" sx={{ mt: 1.5, color: 'text.secondary' }}>
                     Track all incoming funds and deposits.
                  </Typography>
               </motion.div>
             </Grid>
             <Grid item xs={12} md={6}>
               <Box sx={{ maxWidth: '600px', mx: 'auto' }}>
                 <Typography variant="h5" sx={{ fontWeight: 500, mb: 3 }}>Seamless Deposit Management</Typography>
                 <Typography variant="body1" sx={{ color: 'text.secondary', mb: 3 }}>
                   Keep accurate records of all revenue streams. Log deposits with essential details and gain insights into your income trends with automated statistical summaries.
                 </Typography>
                 <List dense>
                   {depositFeatures.map((feature, index) => (
                     <motion.div key={index} variants={itemVariants} custom={index} initial="hidden" animate="visible" transition={{ delay: 0.3 + index * 0.1, duration: 0.5 }}>
                       <ListItem disablePadding sx={{ mb: 1.5, alignItems: 'flex-start' }}>
                         <ListItemIcon sx={{ minWidth: '35px', mt: '4px' }}>
                           {feature.icon}
                         </ListItemIcon>
                         <ListItemText primary={feature.text} primaryTypographyProps={{ variant: 'body1' }} />
                       </ListItem>
                     </motion.div>
                   ))}
                 </List>
               </Box>
             </Grid>
           </Grid>
        </motion.div>

      </Container>
    </FeaturePage>
  );
}

export default FinancialManagementPage; 